import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ShoppingCart, TrendingUp, UserCheck } from 'lucide-react';
import { TimeFilter, PeriodType } from './TimeFilter';
import { UserBreakdownTable, ProductBreakdownTable } from './UserBreakdownTable';
import RegistrationStats from './RegistrationStats';
import { PageHeader } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

interface OrderStats {
  period: {
    start: string;
    end: string;
  };
  devices_sold: number;
  orders_completed: number;
  devices_registered: number;
  sold_by_user: Array<{
    user_name: string;
    user_id: number | null;
    count: number;
  }>;
  sold_by_product: Array<{
    product_name: string;
    product_id: number | null;
    count: number;
  }>;
}

const OrderStats: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && ['7d', '30d', '365d', 'this_month', 'this_year', 'custom'].includes(p)) {
      return p as PeriodType;
    }
    if (searchParams.get('start') && searchParams.get('end')) {
      return 'custom';
    }
    const saved = getUserPreference('orderStats', 'period', '30d');
    if (['7d', '30d', '365d', 'this_month', 'this_year'].includes(saved)) {
      return saved as PeriodType;
    }
    return '30d';
  });
  
  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');
  
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      let url = `/api/stats/orders?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `/api/stats/orders?start=${customStart}&end=${customEnd}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching order stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch order statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, customStart, customEnd]);

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setUserPreference('orderStats', 'period', newPeriod);
    }
    
    const params = new URLSearchParams();
    if (newPeriod === 'custom' && start && end) {
      params.set('start', start);
      params.set('end', end);
      setCustomStart(start);
      setCustomEnd(end);
    } else {
      params.set('period', newPeriod);
      setCustomStart('');
      setCustomEnd('');
    }
    setSearchParams(params);
  };

  const SkeletonTable = () => (
    <div className="bg-white rounded-lg shadow animate-pulse">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="h-6 bg-gray-200 rounded w-48"></div>
      </div>
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-4">
            <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
            <div className="w-12 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Order Statistics"
        subtitle="Detailed breakdown of order fulfillment and device assignments"
        onBack={() => window.history.back()}
      />

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <SkeletonTable />
          <SkeletonTable />
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Devices Sold</h3>
                  <p className="text-3xl font-bold text-green-600">{stats.devices_sold}</p>
                  <p className="text-xs text-gray-500 mt-1">Devices assigned to orders</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Orders Completed</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.orders_completed}</p>
                  <p className="text-xs text-gray-500 mt-1">Orders with all items fulfilled</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Devices Registered</h3>
                  <p className="text-3xl font-bold text-indigo-600">{stats.devices_registered}</p>
                  <p className="text-xs text-gray-500 mt-1">First owner in SeeYou Cloud</p>
                </div>
              </div>
            </div>
          </div>

          {/* Devices Sold by User */}
          <UserBreakdownTable
            title="Order Fulfillment by User"
            data={stats.sold_by_user}
            columns={[{ key: 'count', label: 'Devices Assigned' }]}
            emptyMessage="No devices were assigned to orders in this period"
          />

          {/* Devices Sold by Product */}
          <ProductBreakdownTable
            title="Devices Sold by Product Type"
            data={stats.sold_by_product}
            columns={[{ key: 'count', label: 'Devices Sold' }]}
            emptyMessage="No devices were sold in this period"
          />

          {/* Average Calculation */}
          {stats.sold_by_user.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.sold_by_user.length}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Team Members Active</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(stats.devices_sold / stats.sold_by_user.length)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Avg. Devices per User</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.sold_by_user.length > 0 ? stats.sold_by_user[0].user_name : '-'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Top Performer</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Own request: the order numbers above do not wait for it */}
      <div className="mt-10 border-t border-gray-200">
        <RegistrationStats period={period} customStart={customStart} customEnd={customEnd} />
      </div>
    </div>
  );
};

export default OrderStats;

